import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useQueryState, useInfiniteQuery, useI18n } from '@ywwwtseng/react-kit';
import {
  Typography,
} from '@ywwwtseng/tma-react-kit';
import * as schema from '@/db/schema';
import * as web3 from '@ywwwtseng/web3';
import dayjs from '@/lib/dayjs';
import { useTokenPrice } from '@/hooks/useTokenPrice';
import { useUserBalance } from '@/hooks/useUserBalance';
import { TokenIcon } from '@/components/common/TokenIcon';
import { Operation } from '@/components/common/Operation';
import { ArrowUpOrDown } from '@/components/common/ArrowUpOrDown';
import { useToken } from '@/hooks/useToken';
import {
  displayAmount,
  getBalancePrice,
  getTokenSymbol,
  parseTokenId,
  getTokenActivityType,
} from '@/utils/token';
import { NETWORKS, NETWORK_NAMES, MOONINI_TOKEN_ID } from '@/constants';

export default function TokenScreen({
  params,
}: {
  params: { token_id: string };
}) {
  const networks = useQueryState<string[]>('networks');
  const isMoonini = params.token_id === MOONINI_TOKEN_ID;
  const [selectedNetwork, setSelectedNetwork] = useState<string>(
    params.token_id.includes(':')
      ? parseTokenId(params.token_id).network
      : NETWORKS.BSC
  );
  const { t } = useI18n();
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const token = useToken(params.token_id, selectedNetwork);
  const { value: price } = useTokenPrice(token?.id);
  const { data: logs } = useInfiniteQuery<(typeof schema.logs.$inferSelect)[]>(
    'logs',
    {
      refetchOnMount: true,
      params: {
        token_id: token?.id,
        user_id: me?.id,
        limit: 20,
      },
      enabled: !!me?.id && !!token?.id,
    }
  );

  const balance = useUserBalance(token);

  const tokenSymbol = useMemo(() => {
    if (!token) {
      return undefined;
    }
    return getTokenSymbol(token);
  }, [token]);

  if (!token) {
    return undefined;
  }

  return (
    <div className="flex flex-col items-center justify-start w-full h-full gap-5 py-3 px-5">
      {!params.token_id.includes(':') && (
        <div className="w-full overflow-x-scroll">
          <div className="flex items-center gap-4 whitespace-nowrap min-w-fit">
            {networks?.map((network) => (
              <div
                key={network}
                onClick={() => {
                  setSelectedNetwork(network);
                }}
                className={clsx(
                  'cursor-pointer flex-shrink-0',
                  selectedNetwork !== network && 'opacity-50'
                )}
              >
                <Typography size="2" weight={500}>
                  {NETWORK_NAMES[network as keyof typeof NETWORK_NAMES]}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex h-stack w-full gap-4 px-1 py-1.5 cursor-pointer">
        <div className="w-full flex flex-col items-start">
          <Typography size="5" weight={500}>
            {displayAmount(balance.totalFormatted, { showTinyAmount: false })}
            {tokenSymbol}
          </Typography>

          {isMoonini && t('common.balance_locked_hint', {
            locked: balance.lockedFormatted,
          })}

          {balance.price && (
            <Typography size="2" color="#777777">
              ${balance.price ?? '0.00'}
            </Typography>
          )}
        </div>
        <TokenIcon token={token} width={40} height={40} />
      </div>
      <Operation tokenId={token.id} />
      <div className="flex-1 flex flex-col w-full gap-2 pb-4 overflow-y-auto">
        <Typography
          size="4"
          weight={600}
          i18n="token.token_activity"
          params={{ token: tokenSymbol! }}
        />
        <div className="v-stack w-full card">
          <div className="h-stack w-full pb-2 !justify-between">
            <Typography i18n="token.type_date" size="2" color="#777777" />
            <Typography i18n="common.amount" size="2" color="#777777" />
          </div>
          {logs?.map(({ id, type, amount, created_at }) => (
            <div
              key={id}
              className="h-stack w-full gap-2 px-1 py-1.5 cursor-pointer"
            >
              <ArrowUpOrDown type={type} />
              <div className="v-stack w-full !items-start">
                <Typography
                  size="3"
                  weight={500}
                  noWrap
                  i18n={getTokenActivityType(type)}
                />
                <Typography size="1" color="#777777">
                  {dayjs(created_at).format('YYYY/MM/DD HH:mm')}
                </Typography>
              </div>
              <div className="v-stack w-full ml-auto !items-end">
                <Typography size="3" weight={500}>
                  {displayAmount(web3.utils.formatUnits(amount, token.decimals))}
                </Typography>
                {price && (
                  <Typography size="1" color="#777777">
                    $
                    {displayAmount(
                      getBalancePrice({
                        token: {
                          price,
                          decimals: token.decimals,
                        },
                        amount,
                      })
                    )}
                  </Typography>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
